import i18next from 'i18next';

/*
 * -----------------------------------------------------------------------
 * Internal-team label overrides (KP-2 / KP-3)
 * -----------------------------------------------------------------------
 * Orgs running in `internal-team-mode` use employee-facing terminology:
 *   Journey Planner -> Module Planner
 *   Courses         -> Module Library
 *
 * The platform's per-org terminology mechanism (organization terminology
 * -> courseText/coursesText interpolation) cannot express this: it swaps
 * the *word* "course" inside sentence templates, while these renames
 * replace whole feature titles ("Journey Planner" has no terminology
 * slug at all, and setting the course plural to "Module Library" would
 * corrupt every "{{coursesText}}" sentence). So the renames live here as
 * an i18next resource-bundle override instead.
 *
 * The bundle is applied ONLY when the `internal-team-mode` org config
 * flag is on (see configuration.pinia.setOrganizationConfigKeys), and is
 * re-applied after every CMS bundle load (see locale.pinia
 * getMessagesFromCMS) because the CMS bundle is added with
 * overwrite=true and would otherwise clobber these keys on locale
 * switch. For every other tenant the function is never called, so the
 * translation tree is byte-identical to today.
 */

const en = {
  sidemenu: {
    journeyPlanner: 'Module Planner',
    courses: 'Module Library',
    // KP-61: Teams surface as Departments.
    teams: 'Departments',
  },
  courses: {
    courses: 'Module Library',
  },
  journeyPlanner: {
    journeyPlanners: 'Module Planner',
    journeyPlannerOverview: 'Module Planner Overview',
    journeyPlannerDetails: 'Module Planner Details',
    journeyPlannerItems: 'Module Planner Items',
    journeyPlannerAssign: 'Module Planner Assignees',
    journeyPlannerItemView: 'Module Planner Item',
    journeyPlannerEdit: 'Edit Module Planner',
    createJourneyTab: 'Create Module Planner',
  },
  // KP-61: the learning-groups ("Teams") surface reads as Departments for
  // internal-team orgs — page title, journey-assignees type option, empty
  // state and the create/rename dialogs.
  shared: {
    teams: 'Departments',
  },
  teams: {
    allTeams: 'Departments',
  },
  team: {
    create: 'Create department',
    updateName: 'Rename department',
  },
  noDataMsg: {
    noTeamsFound: 'No departments found',
  },
};

const ar = {
  sidemenu: {
    journeyPlanner: 'مخطط الوحدات',
    courses: 'مكتبة الوحدات',
    // KP-61: Teams surface as Departments.
    teams: 'الأقسام',
  },
  courses: {
    courses: 'مكتبة الوحدات',
  },
  journeyPlanner: {
    journeyPlanners: 'مخطط الوحدات',
    journeyPlannerOverview: 'نظرة عامة على مخطط الوحدات',
    journeyPlannerDetails: 'تفاصيل مخطط الوحدات',
    journeyPlannerItems: 'عناصر مخطط الوحدات',
    journeyPlannerAssign: 'تعيينات مخطط الوحدات',
    journeyPlannerItemView: 'عنصر مخطط الوحدات',
    journeyPlannerEdit: 'تعديل مخطط الوحدات',
    createJourneyTab: 'إنشاء مخطط الوحدات',
  },
  // KP-61: the learning-groups ("Teams") surface reads as Departments for
  // internal-team orgs.
  shared: {
    teams: 'الأقسام',
  },
  teams: {
    allTeams: 'الأقسام',
  },
  team: {
    create: 'إنشاء قسم',
    updateName: 'إعادة تسمية القسم',
  },
  noDataMsg: {
    noTeamsFound: 'لا توجد أقسام',
  },
};

export const internalTeamLabelOverrides = { en, ar };

export const applyInternalTeamLabelOverrides = (): void => {
  (Object.keys(internalTeamLabelOverrides) as Array<'en' | 'ar'>).forEach(
    (lng) => {
      i18next.addResourceBundle(
        lng,
        'translation',
        internalTeamLabelOverrides[lng],
        true,
        true
      );
    }
  );
};
